import React from 'react';
import { Link, graphql, StaticQuery } from 'gatsby';
import { Typography } from 'amphitheatre';
import styled from 'styled-components';
import { StaticImage } from 'gatsby-plugin-image';
import { useInView } from 'react-intersection-observer';

import Layout from 'src/Layout';

import CallToAction from 'src/Components/CallToAction';
import Container from 'src/Components/ContentContainer';
import SatellitePreview from 'src/Components/SatellitePreview';
import SEO from 'src/Components/SEO';

import CommanderPromoVideo from 'src/pages/videos/CommanderPromo.mp4';

import IconCommand from 'src/images/icon_command.svg';
import IconVisualise from 'src/images/icon_visualize.svg';
import IconMonitor from 'src/images/icon_monitor.svg';
import IconAutomate from 'src/images/icon_automate.svg';
import CommanderLogo from 'src/images/logo_commander_left.svg';

import ImageCommand from 'src/images/pigi/website_command.png';
import ImageVisualise from 'src/images/pigi/website_visualize.png';
import ImageMonitor from 'src/images/pigi/website_monitor.png';
import ImageAutomate from 'src/images/pigi/website_automate.png';
import CommanderComposite from 'src/images/pigi/Commander_screenshot_composite.jpg';

import * as Base from 'src/page-styles/base.styled';
import * as Hero from 'src/page-styles/hero.styled';
import * as CustomButton from 'src/page-styles/button.styled';

const { Header } = Typography;

interface ColumnsProps {
  reverse?: boolean;
}

const Columns = styled.div<ColumnsProps>`
  display: flex;
  flex-flow: column;

  @media only screen and (min-width: 800px) {
    flex-flow: row;
    gap: 2rem;

    ${({ reverse }) => {
      if (reverse) return 'flex-direction: row-reverse;';
    }};
  }
`;

const Column = styled.div`
  display: flex;
  flex-flow: column;
  justify-content: center;

  flex-basis: 50%;

  @media only screen and (min-width: 800px) {
    padding: 1rem;
  }
`;

const CAPABILITY_POINTS = [
  {
    icon: IconCommand,
    title: 'Command',
    text: 'Uplink commands to a single spacecraft or an entire fleet using structured, flight-proven workflows for tasking, procedures, manoeuvres, and onboard software operations.',
    image: ImageCommand,
  },
  {
    icon: IconVisualise,
    title: 'Visualise',
    text: 'Move beyond raw telemetry with visual mission views that help operators understand spacecraft state, behaviour, and operational context.',
    image: ImageVisualise,
  },
  {
    icon: IconMonitor,
    title: 'Monitor',
    text: 'Keep watch over spacecraft health and mission activity with live telemetry, automated checks, alerts, and exception-focused monitoring.',
    image: ImageMonitor,
  },
  {
    icon: IconAutomate,
    title: 'Automate',
    text: 'Plan daily activities ahead of time and let Commander coordinate execution across passes, procedures, data delivery, and reports, with operators alerted when action is needed.',
    image: ImageAutomate,
  },
];

/* Fade-up entrance, played once when the element scrolls into view.
   Under prefers-reduced-motion content renders statically. */
const RevealBox = styled.div`
  @media (prefers-reduced-motion: no-preference) {
    opacity: 0;
    transform: translateY(24px);
    transition: opacity 0.7s ease-out, transform 0.7s ease-out;

    &.in {
      opacity: 1;
      transform: none;
    }
  }
`;

interface RevealProps {
  delay?: number;
  children: React.ReactNode;
}

function Reveal({ delay = 0, children }: RevealProps) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.15 });

  return (
    <RevealBox
      ref={ref}
      className={inView ? 'in' : ''}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </RevealBox>
  );
}

const SectionHead = styled.div`
  text-align: center;
  margin-bottom: 2.5rem;
`;

const Eyebrow = styled.div`
  color: ${({ theme }) => theme.primary500};
  font-family: ${({ theme }) => theme.fontHeader};
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 4px;
  text-transform: uppercase;

  margin-bottom: 0.75rem;
`;

const SectionTitle = styled.div`
  color: ${({ theme }) => theme.white};
  font-family: ${({ theme }) => theme.fontHeader};
  font-size: 1.75rem;
  line-height: 2.25rem;
  font-weight: 700;

  @media only screen and (min-width: 768px) {
    font-size: 2.25rem;
    line-height: 2.75rem;
  }
`;

const PointsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;

  @media only screen and (min-width: 1024px) {
    grid-template-columns: 1fr 1fr;
    gap: 2rem;

    /* Break out of the 1300px content container */
    width: 90vw;
    max-width: 1700px;
    margin-left: calc(50% - min(45vw, 850px));
  }
`;

const CardReveal = styled(RevealBox)`
  height: 100%;
`;

const Card = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  height: 100%;

  padding: 1.75rem 1.75rem 1.5rem;

  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;

  transition: transform 0.3s ease, border-color 0.3s ease, background 0.3s ease;

  @media (hover: hover) {
    &:hover {
      transform: translateY(-4px);
      border-color: rgba(254, 198, 10, 0.4);
      background: rgba(255, 255, 255, 0.05);
    }
  }
`;

const VideoPane = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;

  align-self: center;

  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.55),
    0 0 120px rgba(254, 198, 10, 0.07);

  video {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Showcase = styled.div`
  @media only screen and (min-width: 1024px) {
    /* Break out of the 1300px content container */
    width: 88vw;
    max-width: 1600px;
    margin-left: calc(50% - min(44vw, 800px));
  }
`;

const ShowcaseImage = styled.img`
  display: block;
  width: 100%;
  height: auto;

  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 30px 90px rgba(0, 0, 0, 0.55),
    0 0 140px rgba(254, 198, 10, 0.08);
`;

const CapabilityHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1.25rem;
`;

const CapabilityIcon = styled.img`
  height: 64px;
  width: 64px;

  @media only screen and (min-width: 1024px) {
    height: 80px;
    width: 80px;
  }
`;

const CapabilityTitle = styled.div`
  color: ${({ theme }) => theme.white};
  font-family: ${({ theme }) => theme.fontHeader};
  font-size: 1.75rem;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;

  @media only screen and (min-width: 1024px) {
    font-size: 2.25rem;
  }
`;

const CapabilityText = styled.div`
  color: ${({ theme }) => theme.grey300};
  font-family: ${({ theme }) => theme.fontBody};
  font-size: 1.125rem;
  line-height: 1.75rem;
`;

const CapabilityImage = styled.img`
  display: block;
  width: 100%;
  height: auto;

  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.06);

  /* Pin to the card's bottom edge so cards align across a grid row */
  margin-top: auto;
`;

const VideoSplit = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;

  padding: 2rem 0;

  @media only screen and (min-width: 1024px) {
    grid-template-columns: 1fr 2fr;
    gap: 3rem;
    align-items: center;

    /* Break out of the 1300px content container so the video gets
       nearly two thirds of the viewport */
    width: 94vw;
    margin-left: calc(50% - 47vw);
  }
`;

const VideoIntroTitle = styled.div`
  color: ${({ theme }) => theme.white};
  font-family: ${({ theme }) => theme.fontHeader};
  font-size: 1.5rem;
  line-height: 2.25rem;
  font-weight: 700;

  margin-bottom: 1.5rem;

  @media only screen and (min-width: 1200px) {
    font-size: 1.75rem;
    line-height: 2.5rem;
  }
`;

const VideoIntroText = styled.div`
  color: ${({ theme }) => theme.grey300};
  font-family: ${({ theme }) => theme.fontBody};
  font-size: 1.125rem;
  line-height: 1.75rem;
`;

interface CapabilityCardProps {
  icon: string;
  title: string;
  text: string;
  image: string;
  index: number;
}

function CapabilityCard({
  icon,
  title,
  text,
  image,
  index,
}: CapabilityCardProps) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.15 });

  return (
    <CardReveal
      ref={ref}
      className={inView ? 'in' : ''}
      /* Second column trails the first slightly */
      style={{ transitionDelay: `${(index % 2) * 120}ms` }}
    >
      <Card>
        <CapabilityHeader>
          <CapabilityIcon src={icon} alt={title} />
          <CapabilityTitle>{title}</CapabilityTitle>
        </CapabilityHeader>
        <CapabilityText>{text}</CapabilityText>
        <CapabilityImage src={image} alt={`${title} preview`} />
      </Card>
    </CardReveal>
  );
}

function CapabilityGrid() {
  return (
    <PointsGrid>
      {CAPABILITY_POINTS.map((point, i) => (
        <CapabilityCard key={point.title} {...point} index={i} />
      ))}
    </PointsGrid>
  );
}

const CommanderLogoImage = styled.img`
  display: block;
  width: 100%;
  max-width: 700px;
  height: auto;
  margin-bottom: 1rem;
`;

const CustomHeroText = styled(Hero.Text)`
  box-sizing: border-box;

  padding-right: 0px;
  position: relative;

  @media only screen and (min-width: 1024px) {
    padding-right: 286px;
  }

  @media only screen and (min-width: 1200px) {
    padding-right: 400px;
  }
`;

const PigiLogoWrapper = styled.div`
  .pigi-logo {
    position: absolute;
    left: 0;
    top: -152px;
    height: 128px;
    width: 128px;

    @media only screen and (min-width: 1024px) {
      left: auto;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
      height: 256px;
      width: 256px;
    }

    @media only screen and (min-width: 1200px) {
      height: 376px;
      width: 376px;
    }
  }
`;

const ButtonGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;

  @media only screen and (min-width: 768px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
`;

interface Props {
  site: {
    siteMetadata: {
      rawURL: string;
    };
  };
}

function PIGI({ site }: Props) {
  const { rawURL } = site.siteMetadata;

  return (
    <>
      <SEO
        title="Commander | Saber Astronautics"
        description="Commander is Saber Astronautics’ spacecraft control software for mission operations, automation, telemetry monitoring, command execution, and safety of flight."
        url="https://www.saberastro.com/commander"
      />
      <Layout>
        <Hero.Wrapper>
          <Hero.Cover />
          <Hero.Cover />
          <Hero.Cover />
          <StaticImage
            className="hero-image"
            src="../images/pigi/Space Forum Background.png"
            alt="hero"
          />
          <CustomHeroText>
            <CommanderLogoImage src={CommanderLogo} alt="Saber Commander" />
            <Hero.Header style={{ textAlign: 'left', marginBottom: '1rem' }}>
              Our flight-proven spacecraft control software. Now with full fleet
              automation support.
            </Hero.Header>
            <ButtonGroup>
              <Link to="/contact-us">
                <CustomButton.Yellow fill>Get in touch</CustomButton.Yellow>
              </Link>
            </ButtonGroup>
          </CustomHeroText>
        </Hero.Wrapper>
        <Container.Dark
          style={{
            position: 'relative',
            paddingBottom: '2rem',
          }}
        >
          <Base.Gap />
          <Base.Text>
          </Base.Text>
          <Base.Gap />
          <VideoSplit>
            <Reveal>
              <Eyebrow>Saber Commander</Eyebrow>
              <VideoIntroTitle>
                Step into the future of space mission control with Commander.
              </VideoIntroTitle>
              <VideoIntroText>
                Commander gives operators the tools to plan, automate, monitor,
                and control spacecraft operations from a single visual
                environment. Built for missions from research CubeSats to
                complex commercial and government constellations, Commander
                helps teams reduce operator workload while maintaining safety of
                flight across every phase of on-orbit service.
                <br />
                <br />
                Currently supporting active operations for 33 tons of satellites
                on orbit, Commander brings Saber’s mission-control heritage into
                a modern, highly configurable platform for today’s space
                operations teams.
              </VideoIntroText>
            </Reveal>
            <Reveal delay={150}>
              <VideoPane>
                <video
                  src={CommanderPromoVideo}
                  autoPlay
                  muted
                  loop
                  playsInline
                  controls
                />
              </VideoPane>
            </Reveal>
          </VideoSplit>
          <Base.Gap />
          <Reveal>
            <SectionHead>
              <Eyebrow>Capabilities</Eyebrow>
              <SectionTitle>
                Command. Visualise. Monitor. Automate.
              </SectionTitle>
            </SectionHead>
          </Reveal>
          <CapabilityGrid />
          <Base.Gap />
          <Showcase>
            <Reveal>
              <SectionHead>
                <Eyebrow>One platform</Eyebrow>
                <SectionTitle>
                  Your operations stack in one control environment
                </SectionTitle>
              </SectionHead>
              <Base.Paragraph>
                Commander brings spacecraft commanding, telemetry, operations
                planning, automation, alerting, and reporting into a single
                mission control environment. Designed to integrate with the
                spacecraft systems and ground-station providers your mission
                depends on, Commander gives operators one place to plan,
                execute, monitor, and respond.
              </Base.Paragraph>
              <Base.Paragraph>
                Ground-station agnostic by design, Commander can connect with
                Saber’s known provider configurations or be integrated with new
                providers during the pre-flight phase where suitable APIs are
                available.
              </Base.Paragraph>
            </Reveal>
            <Reveal delay={100}>
              <ShowcaseImage
                src={CommanderComposite}
                alt="The Commander mission control interface showing contact timelines, spacecraft attitude and orbit visualisation"
              />
            </Reveal>
          </Showcase>
          <Base.Gap />
        </Container.Dark>
        <SatellitePreview />
        <Container.Dark>
          <CallToAction
            header="Book a Commander Demo"
            linkText="Get in Touch"
            to="/contact-us/"
          />
        </Container.Dark>
      </Layout>
    </>
  );
}

export default function (): JSX.Element {
  return (
    <StaticQuery
      query={graphql`
        {
          site {
            siteMetadata {
              rawURL
            }
          }
        }
      `}
      render={({ site }) => <PIGI site={site} />}
    />
  );
}
